<template>
  <div class="json-formatter">
    <h1>JSON 格式化工具</h1>
    <div class="formatter-container">
      <div class="editor-layout">
        <div class="input-area">
          <div class="area-header">
            <span>输入 JSON</span>
            <el-button size="small" @click="clearAll">清空</el-button>
          </div>
          <el-input
              v-model="inputJson"
              type="textarea"
              :rows="25"
              placeholder="请输入要格式化的 JSON 字符串"
              @input="formatJson"
          />
        </div>

        <div class="control-area">
          <el-button type="primary" @click="formatJson">
            格式化 →
          </el-button>
        </div>

        <div class="output-area">
          <div class="area-header">
            <span>格式化结果</span>
            <el-button size="small" @click="copyResult">复制</el-button>
          </div>
          <div class="json-output" ref="jsonOutputRef">
            <pre v-html="highlightedJson"></pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'

const inputJson = ref('')
const outputJson = ref('')
const jsonOutputRef = ref(null)
const expandedNodes = ref(new Set())

// 修改高亮JSON的函数
const syntaxHighlight = (json) => {
  if (!json) return ''

  try {
    // 如果是字符串类型且不是有效的JSON，直接返回错误信息
    if (typeof json === 'string' && !json.startsWith('{') && !json.startsWith('[')) {
      return `<span class="error">${json}</span>`
    }

    const parsed = typeof json === 'string' ? JSON.parse(json) : json
    const chunks = []

    const processNode = (node, path = '', level = 0) => {
      const indent = '  '.repeat(level)

      if (Array.isArray(node)) {
        const isExpanded = expandedNodes.value.has(path)
        const toggleBtn = `<span class="toggle-btn" data-path="${path}" onclick="window.toggleNode('${path}')">${isExpanded ? '-' : '+'}</span>`

        chunks.push(`${indent}${toggleBtn}[${!isExpanded ? ` ...${node.length} items` : ''}\n`)
        if (isExpanded) {
          node.forEach((item, index) => {
            processNode(item, `${path}[${index}]`, level + 1)
            if (index < node.length - 1) chunks.push(',\n')
          })
          chunks.push(`\n${indent}]`)
        } else {
          chunks.push(`${indent}]`)
        }
      } else if (typeof node === 'object' && node !== null) {
        const keys = Object.keys(node)
        const isExpanded = expandedNodes.value.has(path)
        const toggleBtn = `<span class="toggle-btn" data-path="${path}" onclick="window.toggleNode('${path}')">${isExpanded ? '-' : '+'}</span>`

        chunks.push(`${indent}${toggleBtn}{${!isExpanded ? `${keys.length}` : ''}\n`)
        if (isExpanded) {
          keys.forEach((key, index) => {
            chunks.push(`${indent}  <span class="key">"${key}"</span>: `)
            processNode(node[key], `${path}.${key}`, level + 1)
            if (index < keys.length - 1) chunks.push(',\n')
          })
          chunks.push(`\n${indent}}`)
        } else {
          chunks.push(`${indent}}`)
        }
      } else {
        if (typeof node === 'string') {
          chunks.push(`<span class="string">"${node}"</span>`)
        } else if (typeof node === 'number') {
          chunks.push(`<span class="number">${node}</span>`)
        } else if (typeof node === 'boolean') {
          chunks.push(`<span class="boolean">${node}</span>`)
        } else if (node === null) {
          chunks.push(`<span class="null">null</span>`)
        }
      }
    }

    processNode(parsed)
    return chunks.join('')
  } catch (error) {
    // 如果解析失败，返回错误信息
    return `<span class="error">${json}</span>`
  }
}

const highlightedJson = computed(() => {
  if (!outputJson.value) return ''
  return syntaxHighlight(outputJson.value)
})

const formatJson = () => {
  if (!inputJson.value.trim()) {
    outputJson.value = ''
    return
  }

  try {
    const obj = JSON.parse(inputJson.value)
    outputJson.value = JSON.stringify(obj, null, 2)

    // 默认展开所有节点
    const expandAll = (obj, path = '') => {
      expandedNodes.value.add(path)
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            expandAll(item, `${path}[${index}]`)
          }
        })
      } else if (typeof obj === 'object' && obj !== null) {
        Object.keys(obj).forEach(key => {
          if (typeof obj[key] === 'object' && obj[key] !== null) {
            expandAll(obj[key], `${path}.${key}`)
          }
        })
      }
    }
    expandAll(obj)

    ElMessage.success('格式化成功')
  } catch (error) {
    // 修改错误信息的格式
    outputJson.value = `JSON 格式不正确: ${error.message}`
    ElMessage.warning('JSON 格式不正确')
  }
}

const copyResult = async () => {
  if (!outputJson.value) {
    ElMessage.warning('没有可复制的内容')
    return
  }

  try {
    await navigator.clipboard.writeText(outputJson.value)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

const clearAll = () => {
  inputJson.value = ''
  outputJson.value = ''
  ElMessage.success('已清空')
}

// 添加切换展开/折叠的函数
const toggleNode = (path) => {
  if (expandedNodes.value.has(path)) {
    expandedNodes.value.delete(path)
  } else {
    expandedNodes.value.add(path)
  }
  // 强制更新输出
  outputJson.value = JSON.stringify(JSON.parse(outputJson.value), null, 2)
}

// 将toggleNode函数暴露给window对象
window.toggleNode = toggleNode
</script>

<style scoped>
.json-formatter {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 60px;
}

.editor-layout {
  display: flex;
  gap: 20px;
  align-items: stretch;
  margin-top: 20px;
}

.input-area,
.output-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.area-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 5px;
}

.control-area {
  display: flex;
  align-items: center;
  padding: 0 10px;
  margin-top: 35px;
}

.json-output {
  flex: 1;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 12px;
  background-color: #ffffff;
  overflow: auto;
  height: 600px;
  position: relative;
}

.json-output pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: Consolas, "Courier New", monospace; /* 修改字体族 */
  font-size: 14px;
  line-height: 1.5;
  overflow: auto;
  max-height: 600px;
}

/* 自定义滚动条样式 */
.json-output::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.json-output::-webkit-scrollbar-thumb {
  background-color: #e4e7ed;
  border-radius: 4px;
}

.json-output::-webkit-scrollbar-track {
  background-color: #ffffff;
}

/* 确保输入和输出区域样式一致 */
:deep(.el-textarea__inner) {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  height: 600px !important;
  background-color: #ffffff;
  overflow: auto; /* 输入框也支持滚动 */
}

/* JSON 语法高亮样式 */
:deep(.string) {
  color: #22863a;
}

:deep(.number) {
  color: #005cc5;
}

:deep(.boolean) {
  color: #005cc5;
}

:deep(.null) {
  color: #d73a49;
}

:deep(.key) {
  color: #24292e;
  font-weight: bold;
}

/* 修改展开/折叠按钮样式 */
:deep(.toggle-btn) {
  display: inline-block;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  cursor: pointer;
  color: #f56c6c; /* 红色 */
  margin-right: 4px;
  user-select: none;
  font-weight: bold; /* 加粗 */
  font-size: 16px; /* 稍微调大一点 */
}

:deep(.toggle-btn:hover) {
  color: #ff4d4f; /* hover时的颜色 */
}

/* 添加错误信息的样式 */
:deep(.error) {
  color: #f56c6c;
  font-weight: bold;
}
</style>
